/**
 * Demo customizer — local, ephemeral look & feel overrides for the public demo.
 *
 * Everything here lives only in this browser tab (sessionStorage): it never
 * changes the real build and dies when the Mini App closes. "Restablecer"
 * clears it. Besides the look it holds the creator's content (name, bio,
 * prices, social links, wishlist), which useAppConfig layers over the demo
 * profile, and it can be exported as JSON for the team to build the real app. The overrides are painted as inline CSS
 * variables on <html>, which beats the skin stylesheet rules, and the animated
 * background reads the same values through resolveAppearance().
 */
import { useSyncExternalStore } from 'react'
import { CONSTELLATIONS, type BackgroundId, type ConstellationId, type SkinId } from './skins'

export type RadiusId = 'square' | 'soft' | 'round' | 'pill'
export type FontId =
  | 'anton' | 'bricolage' | 'fraunces' | 'space' | 'outfit' | 'onest'
  | 'bebas' | 'playfair' | 'montserrat' | 'poppins' | 'inter'

export interface DemoCustomization {
  skin?: SkinId
  background?: BackgroundId
  /** Background glow colours (hex) */
  color?: string
  color2?: string
  motion?: number
  density?: number
  radius?: RadiusId
  fontDisplay?: FontId
  fontBody?: FontId
  /** 0.8 – 1.25 */
  fontScale?: number
  /** Brand colour (buttons, accents) */
  brand?: string
  /** Secondary accent */
  accent?: string
  text?: string
  bg?: string
  /** Constellation over the background ('none' removes it) */
  constellation?: ConstellationId
  /** Background reacts to touch */
  interactive?: boolean
  /** The creator's own content: name, bio, prices, social links, wishlist */
  content?: DemoContent
}

export type WishlistProvider = 'throne' | 'wishtender' | 'amazon' | 'buymeacoffee' | 'kofi' | 'other'
export const WISHLIST_PROVIDERS: WishlistProvider[] = ['throne', 'wishtender', 'amazon', 'buymeacoffee', 'kofi', 'other']

export interface DemoContent {
  name?: string
  bio?: string
  /** First-day price and regular monthly price (COP) */
  priceFirst?: number
  priceMonthly?: number
  /** Platform id → URL or @handle; empty string hides a platform the demo shows by default */
  socials?: Record<string, string>
  wishlist?: { enabled?: boolean; provider?: WishlistProvider; url?: string }
  /** Demo only: preview the plans as on the fan's first day or as a returning fan */
  pricingView?: 'first' | 'later'
}

export const RADIUS_SCALE: Record<RadiusId, number> = { square: 0, soft: 0.5, round: 1, pill: 2 }

export interface FontDef {
  id: FontId
  label: string
  family: string
  /** Google Fonts query, only for faces not loaded by index.html */
  load?: string
  kind: 'display' | 'body' | 'both'
}

export const FONTS: FontDef[] = [
  { id: 'anton', label: 'Anton', family: "'Anton', 'Bebas Neue', sans-serif", kind: 'display' },
  { id: 'bebas', label: 'Bebas Neue', family: "'Bebas Neue', 'Anton', sans-serif", load: 'Bebas+Neue', kind: 'display' },
  { id: 'bricolage', label: 'Bricolage', family: "'Bricolage Grotesque', 'Arial Black', sans-serif", kind: 'display' },
  { id: 'playfair', label: 'Playfair', family: "'Playfair Display', Georgia, serif", load: 'Playfair+Display:ital,wght@0,600;0,800;1,600', kind: 'display' },
  { id: 'fraunces', label: 'Fraunces', family: "'Fraunces', Georgia, serif", kind: 'both' },
  { id: 'space', label: 'Space Grotesk', family: "'Space Grotesk', system-ui, sans-serif", kind: 'both' },
  { id: 'montserrat', label: 'Montserrat', family: "'Montserrat', system-ui, sans-serif", load: 'Montserrat:wght@400;600;800', kind: 'both' },
  { id: 'poppins', label: 'Poppins', family: "'Poppins', system-ui, sans-serif", load: 'Poppins:wght@400;600;700', kind: 'both' },
  { id: 'outfit', label: 'Outfit', family: "'Outfit', system-ui, sans-serif", kind: 'body' },
  { id: 'onest', label: 'Onest', family: "'Onest', system-ui, sans-serif", kind: 'body' },
  { id: 'inter', label: 'Inter', family: "'Inter', system-ui, sans-serif", load: 'Inter:wght@400;600', kind: 'body' },
]

const FONT_BY_ID = Object.fromEntries(FONTS.map((f) => [f.id, f])) as Record<FontId, FontDef>

/** One-tap looks: several knobs at once, so a prospect sees a different product in a second. */
export interface Preset {
  id: string
  label: string
  hint: string
  values: DemoCustomization
}

export const PRESETS: Preset[] = [
  { id: 'editorial', label: 'Editorial', hint: 'Serif, claro, esquinas suaves', values: { skin: 'atelier', background: 'silk', radius: 'soft', fontDisplay: 'playfair', fontBody: 'inter' } },
  { id: 'neon', label: 'Neón', hint: 'Oscuro, gaming, cuadrado', values: { skin: 'arcade', background: 'grid', radius: 'square', fontDisplay: 'space', fontBody: 'space' } },
  { id: 'soft', label: 'Soft', hint: 'Rosa, redondo, amable', values: { skin: 'blush', background: 'blobs', color: '#ec4899', color2: '#3b82f6', radius: 'pill', fontDisplay: 'poppins', fontBody: 'poppins' } },
  { id: 'luxury', label: 'Luxury', hint: 'Negro, dorado, impacto', values: { skin: 'noir', background: 'nebula', radius: 'soft', fontDisplay: 'anton', fontBody: 'outfit', accent: '#d4af37' } },
  { id: 'sunset', label: 'Sunset', hint: 'Cálido, aurora, moderno', values: { skin: 'studio-night', background: 'aurora', radius: 'round', fontDisplay: 'bricolage', fontBody: 'onest' } },
]

const STORAGE_KEY = 'demo_custom_v1'
const HEX = /^#?([0-9a-f]{6})$/i

// ─── Store ──────────────────────────────────────────────────────────────────
let state: DemoCustomization = read()
const listeners = new Set<() => void>()

function read(): DemoCustomization {
  if (typeof window === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? sanitize(JSON.parse(raw)) : {}
  } catch {
    return {}
  }
}

function sanitize(v: unknown): DemoCustomization {
  if (!v || typeof v !== 'object') return {}
  const o = v as Record<string, unknown>
  const out: DemoCustomization = {}
  const hex = (x: unknown) => (typeof x === 'string' && HEX.test(x) ? `#${x.replace('#', '').toLowerCase()}` : undefined)
  const num = (x: unknown, min: number, max: number) => (typeof x === 'number' && Number.isFinite(x) ? Math.min(max, Math.max(min, x)) : undefined)
  if (typeof o.skin === 'string') out.skin = o.skin as SkinId
  if (typeof o.background === 'string') out.background = o.background as BackgroundId
  if (typeof o.radius === 'string' && o.radius in RADIUS_SCALE) out.radius = o.radius as RadiusId
  if (typeof o.fontDisplay === 'string' && o.fontDisplay in FONT_BY_ID) out.fontDisplay = o.fontDisplay as FontId
  if (typeof o.fontBody === 'string' && o.fontBody in FONT_BY_ID) out.fontBody = o.fontBody as FontId
  out.color = hex(o.color); out.color2 = hex(o.color2); out.brand = hex(o.brand); out.accent = hex(o.accent); out.text = hex(o.text); out.bg = hex(o.bg)
  out.motion = num(o.motion, 0, 2); out.density = num(o.density, 0.25, 2); out.fontScale = num(o.fontScale, 0.8, 1.25)
  if (typeof o.constellation === 'string' && (CONSTELLATIONS as string[]).includes(o.constellation)) out.constellation = o.constellation as ConstellationId
  if (typeof o.interactive === 'boolean') out.interactive = o.interactive
  const content = sanitizeContent(o.content)
  if (content) out.content = content
  for (const k of Object.keys(out) as (keyof DemoCustomization)[]) if (out[k] === undefined) delete out[k]
  return out
}

function sanitizeContent(v: unknown): DemoContent | undefined {
  if (!v || typeof v !== 'object') return undefined
  const o = v as Record<string, unknown>
  const str = (x: unknown, max: number) => (typeof x === 'string' ? x.slice(0, max) : undefined)
  const price = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? Math.min(Math.round(x), 100_000_000) : undefined)
  const out: DemoContent = { name: str(o.name, 60), bio: str(o.bio, 280), priceFirst: price(o.priceFirst), priceMonthly: price(o.priceMonthly) }
  if (o.socials && typeof o.socials === 'object') {
    const socials: Record<string, string> = {}
    for (const [k, val] of Object.entries(o.socials as Record<string, unknown>).slice(0, 30)) if (/^[a-z0-9_]{1,20}$/.test(k) && typeof val === 'string') socials[k] = val.slice(0, 200)
    out.socials = socials
  }
  if (o.wishlist && typeof o.wishlist === 'object') {
    const w = o.wishlist as Record<string, unknown>
    out.wishlist = {
      enabled: typeof w.enabled === 'boolean' ? w.enabled : undefined,
      provider: typeof w.provider === 'string' && (WISHLIST_PROVIDERS as string[]).includes(w.provider) ? (w.provider as WishlistProvider) : undefined,
      url: str(w.url, 300),
    }
  }
  if (o.pricingView === 'first' || o.pricingView === 'later') out.pricingView = o.pricingView
  for (const k of Object.keys(out) as (keyof DemoContent)[]) if (out[k] === undefined) delete out[k]
  return Object.keys(out).length ? out : undefined
}

/** Parses a pasted export (or a raw customization object) back into a customization. */
export function parseCustomization(json: string): DemoCustomization | null {
  try {
    const v = JSON.parse(json)
    const raw = v && typeof v === 'object' && v.overrides && typeof v.overrides === 'object' ? v.overrides : v
    return sanitize(raw)
  } catch {
    return null
  }
}

function persist() {
  try {
    if (Object.keys(state).length) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    else sessionStorage.removeItem(STORAGE_KEY)
  } catch { /* private mode */ }
}

export function getCustomization(): DemoCustomization { return state }

export function setCustomization(patch: DemoCustomization) {
  const next: DemoCustomization = { ...state, ...patch }
  for (const k of Object.keys(next) as (keyof DemoCustomization)[]) if (next[k] === undefined) delete next[k]
  state = next
  persist()
  listeners.forEach((l) => l())
}

export function resetCustomization() {
  state = {}
  persist()
  listeners.forEach((l) => l())
}

/** Clears the look but keeps the creator's content (used by the one-tap presets). */
export function resetLook() {
  state = state.content ? { content: state.content } : {}
  persist()
  listeners.forEach((l) => l())
}

/** Merges a patch into the content block (undefined removes a field). */
export function setContent(patch: DemoContent) {
  const next: DemoContent = { ...state.content, ...patch }
  for (const k of Object.keys(next) as (keyof DemoContent)[]) if (next[k] === undefined) delete next[k]
  setCustomization({ content: Object.keys(next).length ? next : undefined })
}

/** Replaces the whole customization (import). */
export function replaceCustomization(c: DemoCustomization) {
  state = c
  persist()
  listeners.forEach((l) => l())
}

export function isCustomized(): boolean { return Object.keys(state).length > 0 }

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }

export function useDemoCustomization(): DemoCustomization {
  return useSyncExternalStore(subscribe, getCustomization, getCustomization)
}

// ─── Colour helpers ─────────────────────────────────────────────────────────
type Rgb = [number, number, number]
const toRgb = (h: string): Rgb => {
  const n = parseInt(h.replace('#', ''), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
const toHex = ([r, g, b]: Rgb) => '#' + [r, g, b].map((c) => Math.round(Math.min(255, Math.max(0, c))).toString(16).padStart(2, '0')).join('')
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
const luminance = ([r, g, b]: Rgb) => {
  const f = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4 }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}
const isLight = (h: string) => luminance(toRgb(h)) > 0.35
/** "h s% l%" for the shadcn/ui tokens */
const toHslTriplet = ([r, g, b]: Rgb) => {
  const R = r / 255, G = g / 255, B = b / 255
  const max = Math.max(R, G, B), min = Math.min(R, G, B)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    if (max === R) h = (G - B) / d + (G < B ? 6 : 0)
    else if (max === G) h = (B - R) / d + 2
    else h = (R - G) / d + 4
    h /= 6
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}
const rgba = ([r, g, b]: Rgb, a: number) => `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`

// ─── Apply ──────────────────────────────────────────────────────────────────
const VARS = [
  '--radius-scale', '--radius-pill',
  '--ff-display', '--ff-body', '--ff-italic',
  '--fs-hero', '--fs-title', '--fs-subtitle', '--fs-heading', '--fs-body', '--fs-caption', '--fs-micro',
  '--c-crimson', '--c-crimson-rgb', '--c-crimson-deep', '--c-crimson-ink', '--c-on-crimson', '--primary', '--accent', '--ring',
  '--c-gold', '--c-on-gold',
  '--c-text', '--c-text-dim', '--c-muted', '--foreground', '--card-foreground', '--popover-foreground', '--secondary-foreground', '--muted-foreground',
  '--c-bg', '--c-bg-alt', '--c-surface', '--c-surface-solid', '--c-surface-hi', '--c-sheet', '--c-line', '--c-line-soft', '--c-tint',
  '--background', '--card', '--popover', '--border', '--input', '--muted', '--secondary',
]

const loadedFonts = new Set<string>()
function ensureFont(id?: FontId) {
  const f = id && FONT_BY_ID[id]
  if (!f?.load || loadedFonts.has(f.id)) return
  loadedFonts.add(f.id)
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${f.load}&display=swap`
  document.head.appendChild(link)
}

/** Scales every px/vw number inside a clamp() token, e.g. clamp(55px, 9vw, 73px). */
const scaleSize = (v: string, s: number) => v.replace(/(\d+(?:\.\d+)?)(px|vw|rem)/g, (_, n, u) => `${Math.round(parseFloat(n) * s * 100) / 100}${u}`)

/**
 * Paints the overrides on <html>. `schemeIsDark` is the active skin's scheme,
 * used to derive text/surface tones when only part of the palette changes.
 * Returns the effective scheme (a light background on a dark skin flips it).
 */
export function applyCustomization(c: DemoCustomization, schemeIsDark: boolean, root: HTMLElement = document.documentElement): 'dark' | 'light' {
  const st = root.style
  VARS.forEach((v) => st.removeProperty(v))

  // Shape
  if (c.radius && c.radius !== 'round') {
    st.setProperty('--radius-scale', String(RADIUS_SCALE[c.radius]))
    if (c.radius === 'square') st.setProperty('--radius-pill', '0px')
  }

  // Type
  ensureFont(c.fontDisplay); ensureFont(c.fontBody)
  if (c.fontDisplay) {
    st.setProperty('--ff-display', FONT_BY_ID[c.fontDisplay].family)
    st.setProperty('--ff-italic', FONT_BY_ID[c.fontDisplay].family)
  }
  if (c.fontBody) st.setProperty('--ff-body', FONT_BY_ID[c.fontBody].family)
  if (c.fontScale && Math.abs(c.fontScale - 1) > 0.01) {
    const cs = getComputedStyle(root)
    for (const v of ['--fs-hero', '--fs-title', '--fs-subtitle', '--fs-heading', '--fs-body', '--fs-caption', '--fs-micro']) {
      const base = cs.getPropertyValue(v).trim()
      if (base) st.setProperty(v, scaleSize(base, c.fontScale))
    }
  }

  // Ground colour first: it decides whether we are on a dark or light scheme
  let dark = schemeIsDark
  if (c.bg) {
    dark = !isLight(c.bg)
    const bg = toRgb(c.bg)
    const ink: Rgb = dark ? [255, 255, 255] : [0, 0, 0]
    // Slightly lifted surface: a touch of white on dark grounds, near-white on light ones
    const surfaceSolid = dark ? mix(bg, [255, 255, 255], 0.05) : mix(bg, [255, 255, 255], 0.6)
    st.setProperty('--c-bg', c.bg)
    st.setProperty('--c-bg-alt', toHex(mix(bg, dark ? [0, 0, 0] : [255, 255, 255], 0.5)))
    st.setProperty('--c-surface', rgba(surfaceSolid, 0.74))
    st.setProperty('--c-surface-solid', toHex(surfaceSolid))
    st.setProperty('--c-surface-hi', rgba(mix(surfaceSolid, ink, 0.06), 0.78))
    st.setProperty('--c-sheet', rgba(surfaceSolid, 0.94))
    st.setProperty('--c-line', rgba(ink, dark ? 0.09 : 0.1))
    st.setProperty('--c-line-soft', rgba(ink, 0.045))
    st.setProperty('--c-tint', dark ? '255 255 255' : '20 18 16')
    st.setProperty('--background', toHslTriplet(bg))
    st.setProperty('--card', toHslTriplet(surfaceSolid))
    st.setProperty('--popover', toHslTriplet(surfaceSolid))
    st.setProperty('--border', toHslTriplet(mix(bg, ink, 0.12)))
    st.setProperty('--input', toHslTriplet(mix(bg, ink, 0.1)))
    st.setProperty('--muted', toHslTriplet(mix(bg, ink, 0.12)))
    st.setProperty('--secondary', toHslTriplet(mix(bg, ink, 0.08)))
  }

  // Text: explicit, or re-derived when the ground flipped the scheme
  const text = c.text ?? (c.bg && dark !== schemeIsDark ? (dark ? '#ececec' : '#16130f') : undefined)
  if (text) {
    const tx = toRgb(text)
    const ground = toRgb(c.bg ?? (dark ? '#0a0a0c' : '#f5f2ec'))
    st.setProperty('--c-text', text)
    st.setProperty('--c-text-dim', toHex(mix(tx, ground, 0.35)))
    st.setProperty('--c-muted', toHex(mix(tx, ground, 0.45)))
    const trip = toHslTriplet(tx)
    for (const v of ['--foreground', '--card-foreground', '--popover-foreground', '--secondary-foreground']) st.setProperty(v, trip)
    st.setProperty('--muted-foreground', toHslTriplet(mix(tx, ground, 0.4)))
  }

  // Brand
  if (c.brand) {
    const b = toRgb(c.brand)
    const ground = toRgb(c.bg ?? (dark ? '#0a0a0c' : '#f5f2ec'))
    st.setProperty('--c-crimson', c.brand)
    st.setProperty('--c-crimson-rgb', b.map(Math.round).join(' '))
    st.setProperty('--c-crimson-deep', toHex(mix(b, [0, 0, 0], 0.42)))
    st.setProperty('--c-crimson-ink', toHex(mix(b, ground, 0.86)))
    st.setProperty('--c-on-crimson', isLight(c.brand) ? '#111111' : '#ffffff')
    const trip = toHslTriplet(b)
    st.setProperty('--primary', trip); st.setProperty('--accent', trip); st.setProperty('--ring', trip)
  }
  if (c.accent) {
    st.setProperty('--c-gold', c.accent)
    st.setProperty('--c-on-gold', isLight(c.accent) ? '#111111' : '#ffffff')
  }

  return dark ? 'dark' : 'light'
}

export const hexIsLight = isLight
